import Badge from "@/components/core/badge";
import { ScrollArea } from "@/components/core/scroller";
import { Searchbar } from "@/components/core/searchbar";
import { Content } from "@/components/layouts/content";
import { Header } from "@/components/layouts/header";
import { helpCategoriesQueryOptions } from "@/features/dashboard/api/queries/help-category-queries";
import { IconChevronDown, IconChevronUp, IconNotes } from "@tabler/icons-react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

const helpCategories = [
	{
		id: "getting-started",
		name: "Getting Started",
		slug: "getting-started",
		topics: [
			{ id: "gs-1", title: "Creating an Account", description: "Step-by-step guide to creating an account." },
			{ id: "gs-2", title: "Logging In & Password Reset", description: "How to log in and reset your password." },
			{ id: "gs-3", title: "Navigating the Dashboard", description: "Overview of the dashboard features." },
			{ id: "gs-4", title: "Setting Up Your Profile", description: "Customize your profile settings." },
			{ id: "gs-5", title: "Understanding Account Security", description: "Best practices for securing your account." },
		],
	},
	{
		id: "account-management",
		name: "Account Management",
		slug: "account-management",
		topics: [
			{ id: "am-1", title: "Updating Profile Information", description: "How to update your name, email, and more." },
			{ id: "am-2", title: "Changing Your Password", description: "Steps to update your password securely." },
			{ id: "am-3", title: "Managing Notifications", description: "Customize your email and push notifications." },
			{ id: "am-4", title: "Deleting Your Account", description: "How to permanently delete your account." },
			{ id: "am-5", title: "Two-Factor Authentication", description: "Setting up 2FA for extra security." },
		],
	},
	{
		id: "billing-payments",
		name: "Billing & Payments",
		slug: "billing-payments",
		topics: [
			{ id: "bp-1", title: "Adding a Payment Method", description: "How to add a credit card or PayPal." },
			{ id: "bp-2", title: "Understanding Your Invoice", description: "Breakdown of your billing statements." },
			{ id: "bp-3", title: "Subscription Plans", description: "Details on our plans and pricing." },
			{ id: "bp-4", title: "Refund Policy", description: "When and how you can request a refund." },
			{ id: "bp-5", title: "Managing Auto-Renewal", description: "Enable or disable auto-renewal for subscriptions." },
		],
	},
	{
		id: "troubleshooting",
		name: "Troubleshooting",
		slug: "troubleshooting",
		topics: [
			{ id: "ts-1", title: "Fixing Login Issues", description: "What to do if you can't log in." },
			{ id: "ts-2", title: "App Not Loading", description: "Steps to resolve app loading problems." },
			{ id: "ts-3", title: "Clearing Cache & Cookies", description: "When and how to clear your cache." },
			{ id: "ts-4", title: "Reporting a Bug", description: "How to submit bug reports effectively." },
			{ id: "ts-5", title: "Slow Performance Fixes", description: "Tips to improve loading speed." },
		],
	},
	{
		id: "privacy-security",
		name: "Privacy & Security",
		slug: "privacy-security",
		topics: [
			{ id: "ps-1", title: "Privacy Policy Overview", description: "Key points of our privacy policy." },
			{ id: "ps-2", title: "Managing Data Sharing", description: "How to control what data you share." },
			{ id: "ps-3", title: "Reporting Suspicious Activity", description: "What to do if you see unusual activity." },
			{ id: "ps-4", title: "Blocking & Unblocking Users", description: "How to manage your block list." },
			{ id: "ps-5", title: "Requesting Data Deletion", description: "Steps to request removal of your data." },
		],
	},
];

type Topic = {
	id: string;
	title: string;
	description: string;
	body?: string;
	attachment?: {
		original_url: string;
		mime_type?: string;
	};
};

function TopicDisplay({ selectedTopic }: { selectedTopic: Topic }) {
	return (
		<div className="flex flex-col w-full max-w-[720px] mx-auto">
			<div className="font-bold text-xl mb-2">{selectedTopic.title}</div>
			<div className="text-base text-foreground">{selectedTopic.description}</div>
		</div>
	);
}

function TopicSearch() {
	return (
		<div className="flex flex-col w-full max-w-[720px] mx-auto gap-6">
			<div className="flex flex-col w-full gap-2">
				<div className="text-center flex flex-col w-full">
					<div className="text-center text-xl font-semibold">Help Center</div>
				</div>
				<div className="flex flex-row w-full max-w-[720px]">
					<Searchbar />
				</div>
			</div>
			<div className="flex flex-col w-full gap-4">
				<Link to="/" className="flex flex-row gap-3">
					<div className="inline-flex shrink-0 justify-center items-center w-[44px] h-[44px] rounded-full bg-primary-600/10 text-primary-500">
						<IconNotes stroke={2} size={25} className="" />
					</div>
					<div className="flex flex-col gap-1">
						<Badge className="self-start uppercase" size="xs" color="gray-light">
							Category
						</Badge>
						<div className="flex flex-col w-full">
							<span className="text-base font-semibold text-foreground-dark">How to navigate a help center</span>
							<span className="text-base text-foreground line-clamp-2">
								Understanding how to navigate a help center efficiently can save users valuable time and frustration. A well-structured help center categorizes
								information into relevant sections, making it easier to find answers.
							</span>
						</div>
					</div>
				</Link>
				<Link to="/" className="flex flex-row gap-3">
					<div className="inline-flex shrink-0 justify-center items-center w-[44px] h-[44px] rounded-full bg-primary-600/10 text-primary-500">
						<IconNotes stroke={2} size={25} className="" />
					</div>
					<div className="flex flex-col gap-1">
						<Badge className="self-start uppercase" size="xs" color="gray-light">
							Category
						</Badge>
						<div className="flex flex-col w-full">
							<span className="text-base font-semibold text-foreground-dark">How to navigate a help center</span>
							<span className="text-base text-foreground line-clamp-2">
								Understanding how to navigate a help center efficiently can save users valuable time and frustration. A well-structured help center categorizes
								information into relevant sections, making it easier to find answers.
							</span>
						</div>
					</div>
				</Link>
				<Link to="/" className="flex flex-row gap-3">
					<div className="inline-flex shrink-0 justify-center items-center w-[44px] h-[44px] rounded-full bg-primary-600/10 text-primary-500">
						<IconNotes stroke={2} size={25} className="" />
					</div>
					<div className="flex flex-col gap-1">
						<Badge className="self-start uppercase" size="xs" color="gray-light">
							Category
						</Badge>
						<div className="flex flex-col w-full">
							<span className="text-base font-semibold text-foreground-dark">How to navigate a help center</span>
							<span className="text-base text-foreground line-clamp-2">
								Understanding how to navigate a help center efficiently can save users valuable time and frustration. A well-structured help center categorizes
								information into relevant sections, making it easier to find answers.
							</span>
						</div>
					</div>
				</Link>
			</div>
		</div>
	);
}

export const Route = createFileRoute("/help-center2")({
	component: Page,
	loader: ({ context: { queryClient }, deps }) => {
		return queryClient.ensureQueryData(
			helpCategoriesQueryOptions({
				...deps,
			}),
		);
	},
	loaderDeps: ({ search }) => ({
		search: search,
	}),
});

function Page() {
	const [openCategories, setOpenCategories] = useState<{ [key: string]: boolean }>({});
	const [selectedTopic, setSelectedTopic] = useState<Topic | null>(null);

	const toggleCategory = (slug: string) => {
		setOpenCategories((prev) => ({
			...prev,
			[slug]: !prev[slug],
		}));
	};

	const handleTopicClick = (topic: any) => {
		setSelectedTopic(topic);
	};

	return (
		<Content pageName="Help Center">
			{/*<Header title="Help Center" />*/}
			<div className="flex flex-row w-full pl-[380px]">
				<div className="fixed top-[90px] left-0 bottom-0 flex flex-col w-full max-w-[380px] border-r border-border/50">
					<div className="flex flex-col w-full h-full">
						<div className="flex flex-row w-full px-6 pt-6 pb-3">
							<Searchbar />
						</div>
						<ScrollArea className="h-[calc(100svh-180px)]">
							<div className="flex flex-col w-full px-6 pt-3 pb-6 overflow-y-auto">
								{helpCategories.map((category) => (
									<div key={category.slug} className="flex flex-col w-full">
										<button
											type="button"
											onClick={() => toggleCategory(category.slug)}
											className="flex flex-row w-full items-center justify-between text-left py-1 text-base font-semibold text-foreground-dark hover:text-primary-600"
										>
											<span className="">{category.name}</span>
											{openCategories[category.slug] ? (
												<IconChevronUp className="text-foreground/50" size={18} stroke={2} />
											) : (
												<IconChevronDown className="text-foreground/50" size={18} stroke={2} />
											)}
										</button>

										{openCategories[category.slug] && (
											<div className="flex flex-col w-full pl-1">
												<div className="flex flex-col w-full pl-3 mb-2 border-l border-primary-600">
													{category.topics.map((topic) => (
														<button
															key={topic.id}
															type="button"
															onClick={() => handleTopicClick(topic)}
															className="py-1 text-base text-foreground/70 hover:text-primary-600 text-left"
														>
															{topic.title}
														</button>
													))}
												</div>
											</div>
										)}
									</div>
								))}
							</div>
						</ScrollArea>
					</div>
				</div>

				<div className="flex flex-col items-center w-full h-[calc(100svh-90px)] overflow-hidden">
					<div className="flex flex-col w-full h-full">
						<ScrollArea className="h-[calc(100svh-90px)] p-6">{selectedTopic ? <TopicDisplay selectedTopic={selectedTopic} /> : <TopicSearch />}</ScrollArea>
					</div>
				</div>
			</div>
		</Content>
	);
}
